import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {uuidZodValidator} from "@vesta/global-common-typescript/validators/zodValidators";
import {
    findFormPageByFormId,
    isEventContainerWithEventsPage,
    isJourneyProfilePage,
    PageType,
    type Cta,
    type JourneyProfilePage,
    type WorkspaceConfig,
} from "@vesta/vos-types/modules/workspaceLevel/workspaceUiConfigs/domain/WorkspaceConfig";
import {loadOrCreateWorkspaceConfig} from "~/workspaceConfig/configRuntime";

/**
 * Resolve a config page id by {@link PageType} from the workspace manifest.
 * Missing types are a config error — do not fall back to hardcoded page ids.
 */
export function resolveConfigPageIdByType(
    workspaceId: Uuid | string,
    pageType: PageType | string,
): string {
    const pageId = findConfigPageIdByType(workspaceId, pageType);
    if (pageId === undefined) {
        throw new Error(`No config page found for type ${pageType}`);
    }
    return pageId;
}

export function findPageIdByTypeInConfig(
    config: {pages: Record<string, {id: string; type: string}>},
    pageType: PageType | string,
): string | undefined {
    for (const page of Object.values(config.pages)) {
        if (page.type === pageType) {
            return page.id;
        }
    }
    return undefined;
}

export function findConfigPageIdByType(
    workspaceId: Uuid | string,
    pageType: PageType | string,
): string | undefined {
    const config = loadOrCreateWorkspaceConfig(workspaceId as Uuid);
    return findPageIdByTypeInConfig(config, pageType);
}

/**
 * Pick a JourneyProfile page in this workspace's config.
 *
 * 0 pages → undefined. 1 page → that page (same as {@link findConfigPageIdByType}).
 * 2+ pages and `eventContainerTemplateId` set → the page whose
 * `dispositionProviderEventContainerTemplateId` matches. Otherwise the first page.
 */
export function findConfigJourneyProfilePageId(
    workspaceId: Uuid | string,
    eventContainerTemplateId?: Uuid | string | null,
): string | undefined {
    const config = loadOrCreateWorkspaceConfig(workspaceId as Uuid);
    const journeyProfilePages: Array<JourneyProfilePage> = [];
    for (const page of Object.values(config.pages)) {
        if (isJourneyProfilePage(page) === true) {
            journeyProfilePages.push(page);
        }
    }
    if (journeyProfilePages.length === 0) {
        return undefined;
    }
    const firstPage = journeyProfilePages[0];
    if (firstPage === undefined) {
        return undefined;
    }
    if (journeyProfilePages.length === 1) {
        return firstPage.id;
    }
    if (eventContainerTemplateId !== undefined && eventContainerTemplateId !== null) {
        for (const page of journeyProfilePages) {
            if (page.dispositionProviderEventContainerTemplateId === eventContainerTemplateId) {
                return page.id;
            }
        }
    }
    return firstPage.id;
}

/**
 * Event-container template the journey-profile API needs.
 *
 * URL wins (row's real template). Otherwise the parent leads list, then this
 * page's disposition provider, then the sole configured template, then an
 * optional workspace-specific fallback. Dulux has two templates, so a
 * hardcoded workspace default is unsafe.
 */
export function resolveJourneyProfileEventContainerTemplateId(args: {
    config: WorkspaceConfig;
    page: JourneyProfilePage;
    eventContainerTemplateIdFromUrl?: string;
    fallbackTemplateId?: string;
}): string | undefined {
    const fromUrl = uuidZodValidator.safeParse(args.eventContainerTemplateIdFromUrl);
    if (fromUrl.success === true) {
        return fromUrl.data;
    }

    if (args.page.parentPageId !== null && args.page.parentPageId !== undefined) {
        const parentPage = args.config.pages[args.page.parentPageId];
        if (parentPage !== undefined && isEventContainerWithEventsPage(parentPage) === true) {
            return parentPage.eventContainerTemplateId;
        }
    }

    if (args.page.dispositionProviderEventContainerTemplateId !== undefined) {
        return args.page.dispositionProviderEventContainerTemplateId;
    }

    if (args.config.eventContainerTemplates.length === 1) {
        return args.config.eventContainerTemplates[0]?.id;
    }

    const fallback = uuidZodValidator.safeParse(args.fallbackTemplateId);
    if (fallback.success === true) {
        return fallback.data;
    }

    return undefined;
}

/**
 * Resolve the registered config page targeted by a CTA.
 *
 * `openPage` references a page directly; page-presented `openForm` references
 * the FormPage that owns the configured form. No route or page-id fallback is
 * inferred outside the workspace config.
 */
export function resolveConfigPageIdFromCta(
    config: WorkspaceConfig,
    cta: Cta | undefined,
): string | null {
    if (cta === undefined) {
        return null;
    }
    if (cta.action.type === "openPage") {
        return config.pages[cta.action.pageId]?.id ?? null;
    }
    if (cta.action.type === "openForm") {
        return findFormPageByFormId(config, cta.action.formId)?.id ?? null;
    }
    return null;
}

const PAGE_TYPE_BY_RESERVED_SEGMENT: Record<string, PageType> = {
    leaderboard: PageType.Leaderboard,
    dashboard: PageType.IntellsysDashboard,
    "amazon-dashboard": PageType.AmazonDashboard,
    "google-dashboard": PageType.GoogleDashboard,
    "meta-dashboard": PageType.MetaDashboard,
    "dulux-dashboard": PageType.DuluxDashboard,
    "source-wise-lead-funnel": PageType.AmaronSourceWiseLeadFunnel,
    "amaron-campaign-overview": PageType.AmaronCampaignOverview,
    "bcg-google-dashboard": PageType.AmaronBcgGoogleDashboard,
    "bcg-meta-dashboard": PageType.AmaronBcgMetaDashboard,
    "business-summary": PageType.AmaronBusinessSummary,
    reports: PageType.Reports,
    collections: PageType.Collections,
    "favourite-reports": PageType.FavouriteReports,
    notifications: PageType.Notifications,
    "store-configuration": PageType.StoreConfiguration,
};

function reservedSegmentFromPathname(pathname: string, workspaceId: Uuid): string | null {
    const prefix = `/w/${workspaceId}/`;
    if (pathname.startsWith(prefix) !== true) {
        return null;
    }
    const rest = pathname.slice(prefix.length).split("?")[0] ?? "";
    const segment = rest.split("/").filter((part) => part !== "")[0];
    if (segment === undefined) {
        return null;
    }
    return segment;
}

/**
 * If the URL is a reserved production path (`/dashboard`, `/reports`, …) and
 * the workspace config already has that hosted page, redirect to it.
 * Does not create pages — missing types stay 404 until added in config.
 */
export function resolveReservedConfigPageRedirect(args: {
    pathname: string;
    workspaceId: Uuid;
    userTemplateId: Uuid;
}): {pageKey: Uuid} | null {
    const segment = reservedSegmentFromPathname(args.pathname, args.workspaceId);
    if (segment === null) {
        return null;
    }
    const pageType = PAGE_TYPE_BY_RESERVED_SEGMENT[segment];
    if (pageType === undefined) {
        return null;
    }
    const pageKey = findConfigPageIdByType(args.workspaceId, pageType);
    if (pageKey === undefined) {
        return null;
    }
    const config = loadOrCreateWorkspaceConfig(args.workspaceId);
    const role = config.scaffold[args.userTemplateId];
    if (role === undefined) {
        return null;
    }
    return {
        pageKey: pageKey as Uuid,
    };
}
